import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Trash2, Pencil, ShieldCheck, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERMISSION_LABELS = {
  // employee_portal
  can_view_schedule: "View Schedule",
  can_view_pay_summary: "View Pay Summary",
  can_submit_time: "Submit Time Entries",
  can_view_safety_docs: "View Safety Docs",
  can_view_equipment: "View Equipment",
  can_request_time_off: "Request Time Off",
  can_view_policies: "View Policies",
  can_view_training: "View Training",
  can_view_documents: "View Documents",
  can_view_clients: "View Clients",
  can_create_quotes: "Create Quotes",
  can_view_invoices: "View Invoices",
  can_manage_employees: "Manage Employees",
  can_view_reports: "View Reports",
  can_message: "Message",
  can_view_own_profile: "View Own Profile",
  can_edit_own_profile: "Edit Own Profile",
  can_view_onboarding: "View Onboarding",
  // client_portal
  can_view_quotes: "View Quotes",
  can_accept_quotes: "Accept Quotes",
  can_pay_invoices: "Pay Invoices",
  can_request_callback: "Request Callback",
  can_request_quote: "Request Quote",
};

const EMPTY_FORM = {
  id: null,
  name: "",
  description: "",
  category: "employee_portal",
  permissions: {},
  allows_delegation: false,
  base_persona_key: null,
};

export default function PortalTemplatesTab() {
  const [loading, setLoading] = useState(true);
  const [builtIn, setBuiltIn] = useState([]);
  const [custom, setCustom] = useState([]);
  const [catalog, setCatalog] = useState({ employee_portal: [], client_portal: [] });
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/portal-templates`);
      setBuiltIn(res.data?.built_in || []);
      setCustom(res.data?.custom || []);
      setCatalog(res.data?.permission_catalog || { employee_portal: [], client_portal: [] });
    } catch (e) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = (basePersona) => {
    if (basePersona) {
      setForm({
        id: null,
        name: `${basePersona.name} (Copy)`,
        description: basePersona.description || "",
        category: basePersona.category,
        permissions: { ...basePersona.permissions },
        allows_delegation: !!basePersona.allows_delegation,
        base_persona_key: basePersona.key,
      });
    } else {
      setForm({ ...EMPTY_FORM, permissions: {} });
    }
    setEditorOpen(true);
  };

  const openEdit = (tpl) => {
    setForm({
      id: tpl.id,
      name: tpl.name,
      description: tpl.description || "",
      category: tpl.category,
      permissions: { ...(tpl.permissions || {}) },
      allows_delegation: !!tpl.allows_delegation,
      base_persona_key: tpl.base_persona_key || null,
    });
    setEditorOpen(true);
  };

  const togglePerm = (key) => {
    setForm((f) => ({
      ...f,
      permissions: { ...f.permissions, [key]: !f.permissions[key] },
    }));
  };

  const saveForm = async () => {
    if (!form.name.trim()) {
      toast.error("Template name required");
      return;
    }
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description,
        category: form.category,
        permissions: form.permissions,
        allows_delegation: form.allows_delegation,
        base_persona_key: form.base_persona_key,
      };
      if (form.id) {
        await axios.put(`${API}/portal-templates/${form.id}`, payload);
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/portal-templates`, payload);
        toast.success("Template created");
      }
      setEditorOpen(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to save";
      toast.error(detail);
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await axios.delete(`${API}/portal-templates/${tpl.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const permissionKeys = catalog[form.category] || [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <CardTitle>Portal User Templates</CardTitle>
              <CardDescription>
                Apply these templates when inviting or editing employee &amp; client portal users.
                Built-in personas can be cloned and customized.
              </CardDescription>
            </div>
            <Button onClick={() => openCreate(null)} data-testid="new-template-btn">
              <Plus className="h-4 w-4 mr-1" /> New Custom Template
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" /> Built-in Personas
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="builtin-templates-grid">
              {builtIn.map((p) => (
                <div
                  key={p.key}
                  className="rounded-xl border-2 border-muted-foreground/15 p-4 bg-card"
                  data-testid={`builtin-template-${p.key}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="font-semibold text-sm">{p.name}</p>
                    <div className="flex gap-1 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">
                        {p.category === "employee_portal" ? "Employee" : "Client"}
                      </Badge>
                      {p.allows_delegation && (
                        <Badge variant="secondary" className="text-[10px]">Delegation</Badge>
                      )}
                      {p.customizable && (
                        <Badge variant="outline" className="text-[10px]">Blank</Badge>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">{p.description}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openCreate(p)}
                    data-testid={`clone-template-${p.key}`}
                  >
                    <Copy className="h-3.5 w-3.5 mr-1" /> Clone &amp; Customize
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-2">My Custom Templates</h3>
            {custom.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                No custom templates yet. Clone a built-in or click "New Custom Template" to start fresh.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="custom-templates-grid">
                {custom.map((t) => (
                  <div
                    key={t.id}
                    className="rounded-xl border-2 border-muted-foreground/15 p-4 bg-card"
                    data-testid={`custom-template-${t.id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="font-semibold text-sm">{t.name}</p>
                      <div className="flex gap-1 flex-wrap">
                        <Badge variant="outline" className="text-[10px]">
                          {t.category === "employee_portal" ? "Employee" : "Client"}
                        </Badge>
                        {t.allows_delegation && (
                          <Badge variant="secondary" className="text-[10px]">Delegation</Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">{t.description || "No description"}</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteTemplate(t)} data-testid={`delete-template-${t.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Editor */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? "Edit Template" : "New Custom Template"}</DialogTitle>
            <DialogDescription>
              Permissions toggled here become the default when applying this template to a portal user.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="tpl-name">Name *</Label>
                <Input
                  id="tpl-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  maxLength={120}
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <Label htmlFor="tpl-category">Portal Type</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v, permissions: {} })}
                  disabled={!!form.id}
                >
                  <SelectTrigger id="tpl-category" data-testid="template-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee_portal">Employee Portal</SelectItem>
                    <SelectItem value="client_portal">Client Portal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="tpl-desc">Description</Label>
              <Textarea
                id="tpl-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                data-testid="template-desc-input"
              />
            </div>
            <div className="flex items-center justify-between gap-2 rounded-lg border p-3 bg-muted/30">
              <div>
                <Label className="text-sm font-medium">Allows Delegation</Label>
                <p className="text-xs text-muted-foreground">
                  Users with this template can manage limited settings for OTHER portal users (Owner chooses scope per-user).
                </p>
              </div>
              <Switch
                checked={form.allows_delegation}
                onCheckedChange={(v) => setForm({ ...form, allows_delegation: v })}
                data-testid="template-delegation-switch"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold">Permissions</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Toggle which modules/actions this user can access.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {permissionKeys.map((k) => (
                  <div
                    key={k}
                    className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 bg-card"
                  >
                    <Label htmlFor={`perm-${k}`} className="text-xs cursor-pointer">
                      {PERMISSION_LABELS[k] || k}
                    </Label>
                    <Switch
                      id={`perm-${k}`}
                      checked={!!form.permissions[k]}
                      onCheckedChange={() => togglePerm(k)}
                      data-testid={`perm-toggle-${k}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={saveForm} data-testid="save-template-btn">
              {form.id ? "Save Changes" : "Create Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { PERMISSION_LABELS };
